import { useRef, useEffect } from 'react'
import { Status, Wrapper } from '@googlemaps/react-wrapper'
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBRARIES } from '@/Constants'
import { MAP_STYLES } from '@/Components/GoogleMap/GoogleMapStyle'
import { type LocationSelectionData } from '@/Components/PreferredLocation/LocationSection'

const POLY_STYLE = {
  fillColor: '#AF231C',
  fillOpacity: 0.1,
  strokeColor: '#AF231C',
  strokeWeight: 2,
}

const setZoomForRadius = (radius: number) => {
  if (radius < 1.5) return 14
  if (radius < 2.5) return 13
  if (radius <= 4.5) return 12
  if (radius < 10.1) return 11
  return 10
}

const SummaryLocationMapInner = ({ location }: { location: LocationSelectionData }) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!ref.current) return

    const center = { lat: location.lat, lng: location.lon }

    const gMap = new window.google.maps.Map(ref.current, {
      zoom: location.mode === 'radius' ? setZoomForRadius(location.radiusKm) : 12,
      center,
      styles: MAP_STYLES,
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      zoomControl: false,
      gestureHandling: 'none',
      disableDefaultUI: true,
    })

    if (location.mode === 'radius') {
      new window.google.maps.Marker({ position: center, map: gMap })
      new window.google.maps.Circle({
        center,
        radius: location.radiusKm * 1000,
        map: gMap,
        fillColor: '#AF231C',
        fillOpacity: 0.05,
        strokeColor: '#AF231C',
        strokeWeight: 2,
      })
    } else if (location.polygonCoordinates?.length) {
      new window.google.maps.Polygon({
        paths: location.polygonCoordinates,
        ...POLY_STYLE,
        editable: false,
        draggable: false,
        map: gMap,
      })
      const bounds = new window.google.maps.LatLngBounds()
      location.polygonCoordinates.forEach((coord) =>
        bounds.extend({ lat: coord.lat, lng: coord.lng })
      )
      gMap.fitBounds(bounds, 40)
    }
  }, [])

  return <div ref={ref} style={{ height: '200px', width: '50%', borderRadius: '4px' }} />
}

const SummaryLocationMap = ({ location }: { location: LocationSelectionData }) => {
  const render = (status: Status) => {
    if (status === Status.LOADING)
      return <div className='h-[200px] bg-gray-50 rounded-[4px] animate-pulse' />
    if (status === Status.FAILURE)
      return (
        <div className='h-[200px] bg-gray-50 rounded-[4px] flex items-center justify-center'>
          <p className='text-sm text-gray-400'>Map unavailable</p>
        </div>
      )
    return <SummaryLocationMapInner location={location} />
  }

  return <Wrapper apiKey={GOOGLE_MAPS_API_KEY} libraries={GOOGLE_MAPS_LIBRARIES} render={render} />
}

export default SummaryLocationMap
